import { Component, ErrorInfo, ReactNode } from 'react';

/*
 * Перехват ошибок отрисовки.
 *
 * Без него React при любой ошибке в любом компоненте убирает с экрана всё
 * дерево, и человек видит белый лист: непонятно, сломалось приложение или
 * связь, и нечего сказать при обращении. Здесь на месте упавшего экрана
 * остаётся объяснение, причина и кнопка перезагрузки.
 *
 * Собрано из готовых компонентов дизайн-системы: пустое состояние `.empty`
 * (3.17) и кнопка outline sm (3.1). Своих классов не заводит.
 */

type Props = { children: ReactNode };
type State = { reason: string | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { reason: null };

  static getDerivedStateFromError(error: unknown): State {
    return { reason: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // В журнал браузера — с деревом компонентов: по нему видно, какой экран упал.
    console.error('Экран упал:', error, info.componentStack);
    // И на сервер, иначе о поломке знает только тот, кто на неё смотрит.
    // Молча: если и это не вышло, человеку от второй ошибки легче не станет.
    const where = (info.componentStack ?? '').trim().split('\n')[0]?.trim().slice(0, 200) ?? '';
    void fetch('/api/health/client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: error.message, where }),
    }).catch(() => {});
  }

  render(): ReactNode {
    if (this.state.reason === null) return this.props.children;
    return (
      <div className="empty">
        <b>Экран не открылся</b>
        <div>Обновите страницу. Если повторится — скажите, что было на экране.</div>
        <div className="mono">{this.state.reason}</div>
        <button type="button" className="btn outline sm" onClick={() => window.location.reload()}>
          Обновить
        </button>
      </div>
    );
  }
}
